import { Card, FOCUS_RING, Icon, TooltipProvider } from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MatrixCompetencyRow } from './MatrixCompetencyRow';
import { MatrixEmployeeHeader } from './MatrixEmployeeHeader';
import { MatrixGroupRow } from './MatrixGroupRow';
import {
    MATRIX_AVERAGE_COLUMN_WIDTH,
    MATRIX_AVERAGE_COLUMN_WIDTH_PX,
    MATRIX_COLUMN_GAP,
    MATRIX_EMPLOYEE_COLUMN_WIDTH_PX,
    MATRIX_LABEL_COLUMN_WIDTH,
    MATRIX_LABEL_COLUMN_WIDTH_PX,
    REPORT_SECTION_TITLE,
} from '../reportStyles';
import { createLevelPresentationResolver } from '../utils/levelPresentation';

import type { CompetencyMatrixReport } from '@/types/reports';

import { useAmelioTranslations } from '@/hooks';

interface CompetencyMatrixProps {
    report: CompetencyMatrixReport;
    language: string;
}

interface ScrollState {
    canScrollPrevious: boolean;
    canScrollNext: boolean;
}

const INITIAL_SCROLL_STATE: ScrollState = { canScrollPrevious: false, canScrollNext: false };

/** The label and average columns are sticky: they overlay the scrolled content, so a page-turn of
 * a full visible width would slide two columns' worth of employees underneath them, unread. */
const MATRIX_STICKY_COLUMNS_WIDTH_PX =
    MATRIX_LABEL_COLUMN_WIDTH_PX + MATRIX_AVERAGE_COLUMN_WIDTH_PX;

/** The matrix table: a sticky label column, one column per employee (avatar header, group
 * pills, competency icons when a group is expanded) and a sticky "average" column, inside a
 * horizontally scrollable, arrow-navigable container. Mounts one `TooltipProvider` for every
 * `MatrixCompetencyRow` cell's tooltip below it, instead of one per cell. */
export const CompetencyMatrix = ({ report, language }: CompetencyMatrixProps) => {
    const { t } = useAmelioTranslations();
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [scrollState, setScrollState] = useState<ScrollState>(INITIAL_SCROLL_STATE);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const resolveLevelPresentation = useMemo(
        () => createLevelPresentationResolver(report.levels),
        [report.levels],
    );

    const scale = report.levels.length;

    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        setScrollState({
            canScrollPrevious: container.scrollLeft > 0,
            canScrollNext: container.scrollLeft + container.clientWidth < container.scrollWidth,
        });
    }, []);

    // The employee count (hence the scrollable width) can change on every filter/profile change,
    // so the arrows' disabled state is recomputed whenever the report itself changes, not just on
    // mount.
    useEffect(() => {
        updateScrollState();
    }, [report, updateScrollState]);

    // The container also narrows and widens without the report changing — a window resize, the
    // host's sidebar collapsing — which flips whether the content still overflows.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(updateScrollState);
        observer.observe(container);

        return () => observer.disconnect();
    }, [updateScrollState]);

    const scrollByVisibleWidth = (direction: 1 | -1) => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        // Below the width of the two sticky columns there is no readable page left to turn, so
        // the arrows fall back to advancing one employee rather than scrolling backwards.
        const step = Math.max(
            container.clientWidth - MATRIX_STICKY_COLUMNS_WIDTH_PX,
            MATRIX_EMPLOYEE_COLUMN_WIDTH_PX,
        );

        container.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    const toggleGroup = (groupId: string) => {
        setExpandedGroupId((current) => (current === groupId ? null : groupId));
    };

    return (
        <Card
            glass={'lg'}
            borderRadius={'3xl'}
            dataTestId={'competency-matrix'}
        >
            <div className={'flex items-center justify-between'}>
                <p className={REPORT_SECTION_TITLE}>{t('reports.competencyMatrix.matrix.title')}</p>

                <div className={'flex items-center gap-2'}>
                    <button
                        type={'button'}
                        onClick={() => scrollByVisibleWidth(-1)}
                        disabled={!scrollState.canScrollPrevious}
                        aria-label={t('reports.competencyMatrix.matrix.previous')}
                        className={`${FOCUS_RING} hover:bg-base-100 rounded-full p-2 disabled:opacity-30 disabled:hover:bg-transparent`}
                        data-testid={'competency-matrix-scroll-previous'}
                    >
                        <Icon
                            name={'arrow-arrow-left'}
                            size={'sm'}
                        />
                    </button>

                    <button
                        type={'button'}
                        onClick={() => scrollByVisibleWidth(1)}
                        disabled={!scrollState.canScrollNext}
                        aria-label={t('reports.competencyMatrix.matrix.next')}
                        className={`${FOCUS_RING} hover:bg-base-100 rounded-full p-2 disabled:opacity-30 disabled:hover:bg-transparent`}
                        data-testid={'competency-matrix-scroll-next'}
                    >
                        <Icon
                            name={'arrow-arrow-right'}
                            size={'sm'}
                        />
                    </button>
                </div>
            </div>

            <TooltipProvider>
                {/* Keyboard-scrollable (`tabIndex`), hence a named region: a focus stop that
                    announces nothing leaves a keyboard or screen-reader user with no idea what
                    they just landed on. */}
                <div
                    ref={scrollContainerRef}
                    onScroll={updateScrollState}
                    className={'scrollbar-glass mt-4 overflow-x-auto'}
                    tabIndex={0}
                    role={'region'}
                    aria-label={t('reports.competencyMatrix.matrix.title')}
                    data-testid={'competency-matrix-scroll-container'}
                >
                    <div className={'min-w-max'}>
                        <div className={`${MATRIX_COLUMN_GAP} flex items-end pb-2`}>
                            <div
                                className={`${MATRIX_LABEL_COLUMN_WIDTH} sticky left-0 shrink-0 bg-white/95`}
                            />

                            {report.employees.map((employee) => (
                                <MatrixEmployeeHeader
                                    key={employee.id}
                                    employee={employee}
                                />
                            ))}

                            <div
                                className={`${MATRIX_AVERAGE_COLUMN_WIDTH} sticky right-0 shrink-0 bg-white/95 text-center`}
                            >
                                <span className={'text-base-400 text-xs font-bold uppercase'}>
                                    {t('reports.competencyMatrix.matrix.average')}
                                </span>
                            </div>
                        </div>

                        <div className={'flex flex-col gap-2'}>
                            {report.groups.map((group) => {
                                const isExpanded = expandedGroupId === group.id;
                                const isDimmed = expandedGroupId !== null && !isExpanded;

                                return (
                                    <div
                                        key={group.id}
                                        className={'flex flex-col gap-1'}
                                    >
                                        <MatrixGroupRow
                                            group={group}
                                            employees={report.employees}
                                            scale={scale}
                                            language={language}
                                            isExpanded={isExpanded}
                                            isDimmed={isDimmed}
                                            onToggle={() => toggleGroup(group.id)}
                                            resolveLevelPresentation={resolveLevelPresentation}
                                        />

                                        {isExpanded &&
                                            group.competencies.map((competency) => (
                                                <MatrixCompetencyRow
                                                    key={competency.id}
                                                    competency={competency}
                                                    employees={report.employees}
                                                    scale={scale}
                                                    language={language}
                                                    resolveLevelPresentation={
                                                        resolveLevelPresentation
                                                    }
                                                />
                                            ))}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </TooltipProvider>
        </Card>
    );
};
